import _extends from "@babel/runtime/helpers/extends";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useHistory, useParams } from "react-router-dom";
import moment from "moment";
import mixpanel from "mixpanel-browser";
import styled, { useTheme } from "styled-components";
import { useDispatch, useSelector } from "react-redux";
import { Alert, Badge, Button, Card, Dropdown, Grid, Icon, Spinner, Text } from "~/components-shared";
import { CRUDActionEnum, IntegrationTypeEnum, RoutesEnum, StatusStateEnum } from "~/types/enums";
import lists from "~/lib/lists";
import insightReportsActionCreators from "~/store/insightReports/actions";
import { resolveMonthDays } from "~/lib/helpers";
import insightReportSelectors from "~/store/insightReports/selectors";
import toastsActionCreators from "~/store/toasts/actions";
import integrationSelectors from "~/store/integrations/selectors";
import { InsightsReportActionEnum } from "~/store/insightReports/types";
const StyledGrid = styled(Grid)`
  border-radius: ${_ref => {
  let {
    theme
  } = _ref;
  return theme.shape.borderRadius.primary;
}};
`;
const TopicInsightsInfoCard = _ref2 => {
  let {
    report,
    ...rest
  } = _ref2;
  const {
    colors
  } = useTheme();
  const {
    t
  } = useTranslation();
  const dispatch = useDispatch();
  const history = useHistory();
  const params = useParams();
  const historyReports = useSelector(insightReportSelectors.getHistoryReports);
  const selectedReportId = params.reportId || historyReports[0]?.id;
  const generateStatus = useSelector(insightReportSelectors.getStatusByGenerateReport(report.id));
  const generateError = useSelector(insightReportSelectors.getErrorByGenerateReport(report.id));
  const sendStatus = useSelector(insightReportSelectors.getStatusBySendReport(report.id));
  const sendError = useSelector(insightReportSelectors.getErrorBySendReport(report.id));
  const subscribed = useSelector(insightReportSelectors.getSubscribed);
  const subscribeStatus = useSelector(insightReportSelectors.getSubscribeStatus);
  const subscribeError = useSelector(insightReportSelectors.getSubscribeError);
  const [hoverSubscribe, setHoverSubscribe] = useState(false);
  const inProgress = moment().valueOf() < moment(report.scheduledDate).valueOf();
  const slackIntegration = useSelector(integrationSelectors.getIntegrationByType(IntegrationTypeEnum.SLACK));
  const error = subscribeError || sendError || generateError;
  useEffect(() => {
    if (slackIntegration && subscribeStatus === StatusStateEnum.IDLE) {
      dispatch(insightReportsActionCreators.fetchSubscriptionStarted());
    }
  }, [dispatch, subscribeStatus, slackIntegration]);
  const handleUpdate = () => {
    dispatch(insightReportsActionCreators.generateStarted(report.id));
    mixpanel.track(`Insights Report – Refresh`, {
      reportId: report.id
    });
  };
  const handleEdit = () => {
    dispatch(insightReportsActionCreators.actionChanged(CRUDActionEnum.UPDATE, report.id));
    mixpanel.track(`Insights Report – Update`, {
      reportId: report.id
    });
  };
  const handleSubscribe = () => {
    if (subscribed) {
      dispatch(insightReportsActionCreators.unsubscribeStarted());
      mixpanel.track(`Insights Report – Unsubscribe`);
    } else {
      dispatch(insightReportsActionCreators.subscribeStarted());
      mixpanel.track(`Insights Report – Update`);
    }
  };
  const handleSend = callback => {
    dispatch(insightReportsActionCreators.sendStarted(report.id, _ref3 => {
      let {
        data
      } = _ref3;
      if (data === report.id) {
        dispatch(toastsActionCreators.pushed({
          severity: "success",
          message: t("TopicInsightsInfoCard.reportSent", {
            destination: lists.insights.scheduleDestinations.names[report.scheduleDestination]
          }),
          timeout: 10000
        }));
        callback();
      }
    }));
    mixpanel.track(`Insights Report – Send in Slack`, {
      reportId: report.id
    });
  };
  const handleClickReport = id => {
    history.push(id ? RoutesEnum.INSIGHTS_REPORT_OLD.replace(":reportId", id) : RoutesEnum.INSIGHTS_REPORTS);
    mixpanel.track(`Insights Report – Select`, {
      reportId: report.id
    });
  };
  const handleShareInSlack = () => {
    dispatch(insightReportsActionCreators.actionChanged(InsightsReportActionEnum.SHARE_IN_SLACK, selectedReportId));
    mixpanel.track(`Insights Report – Share in Slack`, {
      reportId: report.id
    });
  };
  return /*#__PURE__*/React.createElement(React.Fragment, null, error && /*#__PURE__*/React.createElement(Alert, {
    severity: "danger",
    s: {
      mb: 6
    },
    fullWidth: true
  }, error), /*#__PURE__*/React.createElement(Card, _extends({
    color: "transparent",
    border: true
  }, rest), /*#__PURE__*/React.createElement(StyledGrid, {
    cols: 8,
    gap: 3,
    template: slackIntegration ? "auto auto auto auto auto 140px 2.7rem" : "auto auto auto auto auto 2.7rem",
    s: {
      pl: 6,
      pr: 4,
      py: 3
    },
    align: "stretch"
  }, /*#__PURE__*/React.createElement(Grid.Cell, null, /*#__PURE__*/React.createElement(Text, {
    element: "span",
    display: "block",
    fontSize: 3,
    color: "textSecondary",
    s: {
      mb: 1
    }
  }, t("TopicInsightsInfoCard.period")), /*#__PURE__*/React.createElement(Dropdown, {
    direction: "bottomLeft",
    customHandler: (toggle, isOpen) => /*#__PURE__*/React.createElement(Button, {
      color: "translucent",
      s: {
        py: 1.5,
        pl: 4,
        pr: 2,
        ml: -3
      },
      onClick: toggle,
      outline: false
    }, /*#__PURE__*/React.createElement(Text, {
      display: "block",
      weight: "bold",
      fontSize: 4
    }, resolveMonthDays(report.dateFrom, report.dateTo, true), /*#__PURE__*/React.createElement(Icon, {
      src: "chevron",
      scale: 5,
      s: {
        ml: 1
      },
      rotate: isOpen ? 0 : 180
    })))
  }, _ref4 => {
    let {
      isOpen,
      onItemClick
    } = _ref4;
    return /*#__PURE__*/React.createElement(Dropdown.List, {
      isOpen: isOpen,
      width: "230px"
    }, historyReports.map((item, index) => /*#__PURE__*/React.createElement(Dropdown.ListItem, {
      key: item.id,
      onClick: () => {
        handleClickReport(index === 0 ? undefined : item.id);
        onItemClick();
      },
      selected: item.id === report.id
    }, /*#__PURE__*/React.createElement(Grid, {
      cols: 2,
      template: "1fr auto",
      fullWidth: true
    }, /*#__PURE__*/React.createElement(Text, {
      weight: "semiBold"
    }, resolveMonthDays(item.dateFrom, item.dateTo, true)), !!item.scheduledDate && /*#__PURE__*/React.createElement(Badge, {
      fontSize: 3,
      color: "grey",
      s: {
        py: 0.5
      },
      rounded: true,
      style: {
        minWidth: "40px",
        textAlign: "center"
      }
    }, moment(item.scheduledDate).format("ddd"))))));
  })), /*#__PURE__*/React.createElement(Grid.Cell, null, /*#__PURE__*/React.createElement(Text, {
    element: "span",
    display: "block",
    fontSize: 3,
    color: "textSecondary",
    s: {
      mb: 2.5
    }
  }, t("TopicInsightsInfoCard.deliveryDate")), /*#__PURE__*/React.createElement(Text, {
    display: "block",
    weight: "bold",
    fontSize: 4
  }, moment(report.scheduledDate).format("ddd, MMM Do [at] hh:mma"))), /*#__PURE__*/React.createElement(Grid.Cell, null, /*#__PURE__*/React.createElement(Text, {
    element: "span",
    display: "block",
    fontSize: 3,
    color: "textSecondary",
    s: {
      mb: 2.5
    }
  }, t("TopicInsightsInfoCard.lastUpdated")), /*#__PURE__*/React.createElement(Text, {
    display: "inline-block",
    weight: "bold",
    fontSize: 4
  }, report.generatedAt ? moment(report.generatedAt).local().startOf("seconds").fromNow() : "Never"), inProgress && /*#__PURE__*/React.createElement(Button, {
    onClick: () => handleUpdate(),
    loading: generateStatus === StatusStateEnum.LOADING || report.reportStatus?.status === StatusStateEnum.LOADING,
    color: "transparent",
    s: {
      p: 1.5,
      ml: 1,
      mt: -1.5
    },
    display: "inline-block",
    "data-tooltip-id": "general-tooltip",
    "data-tooltip-content": t("TopicInsightsInfoCard.updateNow")
  }, /*#__PURE__*/React.createElement(Icon, {
    src: "refresh",
    scale: 5
  }))), /*#__PURE__*/React.createElement(Grid.Cell, null, /*#__PURE__*/React.createElement(Text, {
    element: "span",
    display: "block",
    fontSize: 3,
    color: "textSecondary",
    s: {
      mb: 2.5
    }
  }, t("TopicInsightsInfoCard.exclusions")), /*#__PURE__*/React.createElement(Text, {
    display: "block",
    weight: "bold",
    fontSize: 4
  }, report.excludedTopics.length || "-")), /*#__PURE__*/React.createElement(Grid.Cell, null, /*#__PURE__*/React.createElement(Text, {
    element: "span",
    display: "block",
    fontSize: 3,
    color: "textSecondary",
    s: {
      mb: 2.5
    }
  }, t("TopicInsightsInfoCard.status")), /*#__PURE__*/React.createElement(Text, {
    display: "block",
    weight: "bold",
    fontSize: 4
  }, inProgress ? t("TopicInsightsInfoCard.inProgress") : t("TopicInsightsInfoCard.complete"))), slackIntegration && /*#__PURE__*/React.createElement(Grid.Cell, null, /*#__PURE__*/React.createElement(Text, {
    element: "span",
    display: "block",
    fontSize: 3,
    color: "textSecondary",
    s: {
      mb: 1.5
    }
  }, t("TopicInsightsInfoCard.receiveInSlack")), /*#__PURE__*/React.createElement(Button, {
    onClick: handleSubscribe,
    onMouseEnter: () => setHoverSubscribe(true),
    onMouseLeave: () => setHoverSubscribe(false),
    loading: subscribeStatus === StatusStateEnum.LOADING,
    fontSize: 3,
    s: {
      pl: 0,
      pr: 2,
      py: 1.5
    },
    color: subscribed ? "translucent" : "primary",
    maxWidth: "140px",
    fullWidth: true
  }, subscribed && /*#__PURE__*/React.createElement(Icon, {
    src: "tick",
    s: {
      mr: 1,
      mt: -0.5
    }
  }), subscribed ? hoverSubscribe ? t("TopicInsightsInfoCard.unsubscribe") : t("TopicInsightsInfoCard.subscribed") : t("TopicInsightsInfoCard.subscribe"))), /*#__PURE__*/React.createElement(Grid.Cell, {
    align: "center"
  }, /*#__PURE__*/React.createElement(Dropdown, {
    direction: "bottomLeft",
    display: "inline-block",
    handlerProps: {
      s: {
        p: 2
      },
      "data-tooltip-id": "general-tooltip",
      "data-tooltip-content": t("common.actions.options")
    },
    handler: /*#__PURE__*/React.createElement(Icon, {
      scale: 5,
      src: "dots-vertical",
      color: colors.text.primary,
      display: "block"
    }),
    fullWidth: true
  }, _ref5 => {
    let {
      isOpen,
      onItemClick
    } = _ref5;
    return /*#__PURE__*/React.createElement(Dropdown.List, {
      isOpen: isOpen
    }, historyReports[0]?.id === selectedReportId && /*#__PURE__*/React.createElement(Dropdown.ListItem, {
      onClick: () => {
        handleEdit();
        onItemClick();
      }
    }, /*#__PURE__*/React.createElement(Icon, {
      src: "pencil",
      scale: 4.5,
      s: {
        mr: 2
      },
      color: "primary"
    }), t("common.actions.edit")), slackIntegration && /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(Dropdown.ListItem, {
      onClick: () => {
        handleSend(() => {
          onItemClick();
        });
      }
    }, sendStatus === StatusStateEnum.LOADING ? /*#__PURE__*/React.createElement(Spinner, {
      scale: 4.5,
      s: {
        mr: 2
      },
      color: "primary"
    }) : /*#__PURE__*/React.createElement(Icon, {
      src: "send",
      scale: 4.5,
      s: {
        mr: 2
      },
      color: "primary"
    }), t("common.actions.send"), " to", " ", lists.insights.scheduleDestinations.names[report.scheduleDestination]), /*#__PURE__*/React.createElement(Dropdown.ListItem, {
      onClick: () => {
        handleShareInSlack();
        onItemClick();
      }
    }, /*#__PURE__*/React.createElement(Icon, {
      src: "share",
      scale: 4.5,
      s: {
        mr: 2
      },
      color: "primary"
    }), t("TopicInsightsInfoCard.shareInSlack"))));
  })))));
};
export default TopicInsightsInfoCard;